import { Select, type SelectOption } from '@shinetools/lumen-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { fetchCountries } from '@modules-deprecated/app/countries/query-api'
import { Country } from '@modules-deprecated/app/countries/types'

import { QueryKeys } from '../../../enums/queryKeys'
import { CountryIsoCode } from '../../../types/countryIsoCode'
import { getCurrentLocale } from '../../../utils'
import { CountryFlag } from '../../CountryFlag'

interface CountryDropdownProps {
  countryId?: string
  error?: string
  label?: string
  onSelect?: (countryId?: string, country?: Country) => void
}

// Lumen equivalent of `CountrySelect` (built on the legacy @billy/design-system Select).
// Use this one in views already built with @shinetools/lumen-react components.
export const CountryDropdown = ({ countryId, error, label, onSelect }: CountryDropdownProps) => {
  const { t } = useTranslation()
  const locale = getCurrentLocale()
  const { data, isLoading } = useQuery([QueryKeys.Countries, locale], () => fetchCountries(locale))

  const countries = useMemo(() => data?.countries ?? [], [data?.countries])

  const options = useMemo<SelectOption[]>(
    () =>
      countries.map((country) => ({
        icon: <CountryFlag countryCode={country.icon as CountryIsoCode} />,
        label: country.name,
        value: country.id,
      })),
    [countries],
  )

  const handleChange = useCallback(
    (selectedCountryId: string) => {
      const country = countries.find(({ id }) => id === selectedCountryId)
      onSelect?.(selectedCountryId, country)
    },
    [countries, onSelect],
  )

  return (
    <Select
      disabled={isLoading}
      error={error}
      label={label ?? t('contacts.form.country')}
      onChange={handleChange}
      options={options}
      placeholder={t('app.countries.country_selector.select_country')}
      searchable
      size="small"
      value={countryId ?? ''}
    />
  )
}
